/**
 * `console.warn` / `console.error` capture for the telemetry `diagnostics` group.
 * Wraps the two methods once, only while the group is on and never in a
 * DEV build (the wrapper would hide call sites in devtools); `stop()` restores them.
 *
 * Each line keeps its first argument only (a string as written, or an Error's name and
 * message), scrubbed (300 chars; URLs, emails and token-shaped runs replaced), and the
 * top 5 stack frames. An identical line repeats at most once a
 * minute: the first is recorded at once, later ones in that minute are counted and
 * recorded together when the minute ends. Nothing is captured while the telemetry
 * code itself is running (`inTelemetry()`), so a log line from inside it can never
 * feed back into it.
 *
 * The friction group also starts the wrapper, to spot error clicks: every
 * `console.error` is announced on lib/telemetry/activity.js (a kind, never the text),
 * except a React development warning (lib/telemetry/reactDevWarning.js; only the
 * integration debug build logs them), but lines are recorded only while `recording()`
 * (the diagnostics group) holds — dev warnings included.
 */
import { noteActivity } from "../lib/telemetry/activity.js";
import { frameLines, scrubFreeText } from "../lib/telemetry/sanitize.js";
import { inTelemetry } from "../lib/telemetry/guard.js";
import { isReactDevWarning } from "../lib/telemetry/reactDevWarning.js";

export const MESSAGE_MAX = 300;
export const FRAMES_MAX = 5;
export const REPEAT_WINDOW_MS = 60_000;

/** `stack`'s top frames, scrubbed (see lib/telemetry/sanitize.js `frameLines`). */
export function topFrames(stack, max = FRAMES_MAX, skip = 0) {
  return frameLines(stack, max, skip);
}

/**
 * The first argument only: a string as written, an Error's name and message. Later
 * arguments (response text, names, objects) are never recorded.
 */
function messageOf(args) {
  const first = args[0];
  if (first instanceof Error) return `${first.name}: ${first.message}`;
  if (typeof first === "string") return first;
  return first === undefined ? "" : `[${Array.isArray(first) ? "array" : typeof first}]`;
}

/** Create the capture; `record(event)` receives each `console.*` event while `recording()`. */
export function createConsoleCapture({
  record,
  recording = () => true,
  target = typeof console !== "undefined" ? console : null,
  dev = !!(import.meta.env && import.meta.env.DEV),
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
} = {}) {
  let originals = null;
  /** Lines seen this minute: key → `{event, count}` (count = repeats not yet recorded). */
  let seen = new Map();
  let capturing = false;

  function emit(event) {
    try {
      record(event);
    } catch {
      /* never surface */
    }
  }

  function capture(level, args) {
    if (capturing || inTelemetry()) return;
    capturing = true;
    try {
      if (level === "error" && !isReactDevWarning({ message: args[0], args }))
        noteActivity("error");
      if (!recording()) return;
      const err = args.find((a) => a instanceof Error);
      const message = scrubFreeText(messageOf(args), MESSAGE_MAX);
      // Without an Error argument, the caller's stack: skip this function and the wrapper.
      const frames = err ? topFrames(err.stack) : topFrames(new Error().stack, FRAMES_MAX, 2);
      const event = { event: `console.${level}`, message, frames };
      const key = `${level}|${message}`;
      const prior = seen.get(key);
      if (prior) {
        prior.count += 1;
        return;
      }
      emit({ ...event, count: 1 });
      const entry = { event, count: 0 };
      seen.set(key, entry);
      const windowEnd = () => {
        // A capture restarted since has its own entry for this line: leave it alone.
        if (seen.get(key) !== entry) return;
        if (entry.count === 0) {
          seen.delete(key); // a quiet window: the next one records at once
          return;
        }
        // Repeats are recorded once per window, and the line stays throttled for
        // another one.
        emit({ ...entry.event, count: entry.count });
        entry.count = 0;
        setTimeoutImpl(windowEnd, REPEAT_WINDOW_MS);
      };
      setTimeoutImpl(windowEnd, REPEAT_WINDOW_MS);
    } catch {
      /* never surface */
    } finally {
      capturing = false;
    }
  }

  function wrap(level) {
    const original = target[level];
    return function (...args) {
      capture(level === "warn" ? "warn" : "error", args);
      return original.apply(this, args);
    };
  }

  return {
    /** Wrap `console.warn` / `console.error`; idempotent, never in DEV. */
    start() {
      if (originals || !target || dev) return;
      if (typeof target.warn !== "function" || typeof target.error !== "function") return;
      originals = { warn: target.warn, error: target.error };
      target.warn = wrap("warn");
      target.error = wrap("error");
    },
    /** Restore the originals; idempotent. */
    stop() {
      if (!originals) return;
      target.warn = originals.warn;
      target.error = originals.error;
      originals = null;
      seen = new Map();
    },
    get started() {
      return originals !== null;
    },
  };
}
